import apiService from '../../../services/api-client';

export interface GeneralData {
  total_skus: number;
  total_units: number;
  inventory_value: number;
  skus_without_stock: number;
  skus_in_alert: number;
  avg_stock_per_sku: number;
}

export interface CategoryValue {
  category: string;
  skus: number;
  units: number;
  value: number;
}

export interface SupplierValue {
  supplier: string;
  skus: number;
  units: number;
  value: number;
}

export interface EconomicData {
  inventory_value: number;
  avg_value_per_sku: number;
  top_categories_by_value: CategoryValue[];
  top_suppliers_by_value: SupplierValue[];
}

export interface RiskItem {
  risk: string;
  skus: number;
  value: number;
  action: string;
}

export interface HealthData {
  stock_cero: number;
  stock_critico: number;
  sobre_stock: number;
  baja_rotacion: number;
  devoluciones: number;
  venta_sin_stock: number;
  ajuste_stock: number;
  alert_date: string;
  risks?: RiskItem[];
}

export interface OrderSummary {
  order_number: string;
  items: number;
  units: number;
  date_of_receipt: string;
}

export interface TransitData {
  active_orders: number;
  units_in_transit: number;
  total_items: number;
  next_reception: string;
  orders_summary: OrderSummary[];
}

export interface WarehouseStock {
  warehouse: string;
  skus: number;
  units: number;
  value: number;
}

export interface ProductByValue {
  sku: string;
  name: string;
  stock: number;
  cost: number;
  value: number;
}

export interface ProductByQuantity {
  sku: string;
  name: string;
  stock: number;
  cost: number;
  value: number;
}

export interface DistributionData {
  stock_by_warehouse: WarehouseStock[];
  top_products_by_value: ProductByValue[];
  top_products_by_quantity: ProductByQuantity[];
  stock_by_category: CategoryValue[];
}

export interface FiltersData {
  warehouses: string[];
  categories: string[];
  suppliers: string[];
  alert_dates?: string[];
}

export interface InventoryAgeRanges {
  '1_7d': number;
  '8_30d': number;
  '31_60d': number;
  '61_90d': number;
  '90_plus': number;
}

export interface InventoryAgeItem {
  metric: string;
  metric_id: string;
  ranges: InventoryAgeRanges;
  total: number;
}

export interface InventoryAgeProduct {
  sku: string;
  name: string;
  stock: number;
  cost: number;
  days_in_alert: number;
  category?: string;
}

export interface InventoryAgeData {
  summary: InventoryAgeItem[];
  top_by_metric: Record<string, InventoryAgeProduct[]>;
}

export interface InventoryAgeDetailResponse {
  metric: string;
  metric_id: string;
  alert_date: string;
  total: number;
  products: InventoryAgeProduct[];
}

export interface ReportsDashboardResponse {
  status: string;
  company_id: number;
  integration: string;
  general: GeneralData;
  economic: EconomicData;
  health: HealthData;
  transit: TransitData | null;
  distribution: DistributionData;
  rotation: RotationData | null;
  filters: FiltersData;
  inventory_age?: InventoryAgeData;
}

export interface RotationProduct {
  sku: string;
  name: string;
  stock: number;
  category: string;
  total_sold_30d: number;
  avg_daily_sales: number;
  days_coverage: number;
}

export interface RotationData {
  period_days: number;
  total_products_with_sales: number;
  total_products_without_sales: number;
  avg_days_coverage: number;
  rotation_rate: number;
  cogs: number;
  inventory_value_for_rotation: number;
  top_fastest_rotation: RotationProduct[];
  top_slowest_rotation: RotationProduct[];
}

export interface RotationDetailProduct {
  sku: string;
  name: string;
  stock: number;
  category: string;
  cost: number;
  total_sold_30d: number;
  avg_daily_sales: number;
  days_coverage: number;
}

export interface RotationDetailResponse {
  title: string;
  sort_type: string;
  period_days: number;
  total: number;
  products: RotationDetailProduct[];
}

export interface ProductsDetailProduct {
  sku: string;
  name: string;
  category: string;
  stock: number;
  cost: number;
  value: number;
}

export interface ProductsDetailResponse {
  title: string;
  sort_type: string;
  total: number;
  products: ProductsDetailProduct[];
}

export interface InventoryEvolutionDay {
  date: string;
  total_skus: number;
  total_units: number;
  inventory_value: number;
  total_sold: number;
  total_returned: number;
}

export interface InventoryEvolutionResponse {
  evolution: InventoryEvolutionDay[];
  total_days: number;
  date_range: { start: string; end: string };
}

export const getInventoryEvolution = async (
  companyId: string,
  days: number = 90
): Promise<InventoryEvolutionResponse> => {
  const api = apiService();
  const res = await api.get(`/reports/${companyId}/inventory-evolution`, { params: { days } });
  return res.data;
};

export const getReportsDashboard = async (
  companyId: string,
  params?: { warehouse?: string; category?: string; supplier?: string; start_date?: string; end_date?: string }
): Promise<ReportsDashboardResponse> => {
  const api = apiService();
  const queryParams: Record<string, string> = {};
  if (params?.warehouse) queryParams.warehouse = params.warehouse;
  if (params?.category) queryParams.category = params.category;
  if (params?.supplier) queryParams.supplier = params.supplier;
  if (params?.start_date) queryParams.start_date = params.start_date;
  if (params?.end_date) queryParams.end_date = params.end_date;
  const res = await api.get(`/reports/${companyId}/dashboard`, { params: queryParams });
  return res.data;
};

export const getInventoryAgeDetail = async (
  companyId: string,
  metricId: string,
  alertDate?: string
): Promise<InventoryAgeDetailResponse> => {
  const api = apiService();
  const params: Record<string, string> = {};
  if (alertDate) params.alert_date = alertDate;
  const res = await api.get(`/reports/${companyId}/inventory-age/${metricId}`, { params });
  return res.data;
};

export const getRotationDetail = async (
  companyId: string,
  sortType: 'fastest' | 'slowest',
  params?: { warehouse?: string; category?: string; supplier?: string }
): Promise<RotationDetailResponse> => {
  const api = apiService();
  const queryParams: Record<string, string> = {};
  if (params?.warehouse) queryParams.warehouse = params.warehouse;
  if (params?.category) queryParams.category = params.category;
  if (params?.supplier) queryParams.supplier = params.supplier;
  const res = await api.get(`/reports/${companyId}/rotation-detail/${sortType}`, { params: queryParams });
  return res.data;
};

export const getProductsDetail = async (
  companyId: string,
  sortType: 'by_value' | 'by_quantity',
  params?: { warehouse?: string; category?: string; supplier?: string }
): Promise<ProductsDetailResponse> => {
  const api = apiService();
  const queryParams: Record<string, string> = {};
  if (params?.warehouse) queryParams.warehouse = params.warehouse;
  if (params?.category) queryParams.category = params.category;
  if (params?.supplier) queryParams.supplier = params.supplier;
  const res = await api.get(`/reports/${companyId}/products-detail/${sortType}`, { params: queryParams });
  return res.data;
};
